import { Link } from "react-router-dom";
import { Check, ArrowRight, Phone } from "lucide-react";
import Seo from "@/components/Seo";
import { PageHero, Reveal } from "@/components/Primitives";
import { PRICING, BUSINESS } from "@/data/site";

const BENEFITS = [
  ["Predictable monthly care", "A set number of bookings each month, so date nights, meetings and errands are always covered."],
  ["Priority placement", "Members get first access to our most-requested, top-rated sitters."],
  ["Preferred, locked rates", "Consistent $20/hr sitter pay with no surprise pricing."],
  ["Flexible month to month", "Life happens — talk to us about adjusting your plan as your needs change."],
  ["One trusted team", "The same coordinators and familiar, 11-step screened sitters your kids already love."],
  ["Add bookings anytime", "Need more than your plan? Add extra bookings for just $15 each."],
];

export default function MembershipBenefits() {
  return (
    <>
      <Seo
        title="Membership Benefits"
        description="The benefits of a Mom's Best Friend Sitters membership — DFW's first-of-its-kind subscription sitter service. Predictable monthly care, priority placement and preferred rates."
        breadcrumbs={[{ name: "Home", item: "/" }, { name: "Pricing", item: "/pricing" }, { name: "Membership Benefits", item: "/pricing/benefits" }]}
      />
      <PageHero
        overline="Memberships"
        title="The benefits of an MBF membership"
        subtitle="Our first-of-its-kind subscription sitter service — trusted, familiar care that fits your family's rhythm, month after month."
      />

      <section className="container-mbf py-20 md:py-28">
        <div className="grid sm:grid-cols-2 gap-6 max-w-4xl">
          {BENEFITS.map(([t, d], i) => (
            <Reveal key={t} delay={i * 0.05}>
              <div className="card-warm p-7 flex gap-4" data-testid={`benefit-${i}`}>
                <span className="w-10 h-10 rounded-full bg-mint/20 text-mint flex items-center justify-center shrink-0"><Check className="w-5 h-5" /></span>
                <div><h3 className="font-bold text-espresso mb-1">{t}</h3><p className="text-sm text-stoneish leading-relaxed">{d}</p></div>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="mt-14 grid md:grid-cols-2 gap-6 max-w-4xl">
          {PRICING.map((tier) => (
            <div key={tier.id} className={`rounded-[2rem] p-8 border-2 ${tier.featured ? "bg-espresso text-parchment border-espresso shadow-pop" : "bg-white border-[#F1E6D0] shadow-soft"}`} data-testid={`benefits-tier-${tier.id}`}>
              {tier.featured && <span className="text-xs uppercase tracking-widest text-gold font-bold">Most popular</span>}
              <h3 className={`font-display font-bold text-2xl mt-1 ${tier.featured ? "text-white" : "text-espresso"}`}>{tier.name}</h3>
              <div className="mt-2 mb-3"><span className="font-display font-bold text-4xl">{tier.price}</span><span className={tier.featured ? "text-parchment/70" : "text-stoneish"}>{tier.period}</span></div>
              <p className={`text-sm ${tier.featured ? "text-parchment/80" : "text-stoneish"}`}>{tier.desc}</p>
            </div>
          ))}
        </div>

        <p className="text-sm text-stoneish mt-6 max-w-2xl">Each service type also includes specialized pricing — visit the service page that fits your needs, or ask our team for a full, transparent breakdown.</p>

        <div className="mt-8 flex flex-col sm:flex-row gap-3">
          <Link to="/book" className="btn-accent" data-testid="benefits-request-cta">Start a request <ArrowRight className="w-4 h-4" /></Link>
          <a href={BUSINESS.phoneHref} className="btn-outline" data-testid="benefits-phone-link"><Phone className="w-4 h-4" /> {BUSINESS.phone}</a>
        </div>
      </section>
    </>
  );
}
